<aside id="side-menu" class="menu-content" [ngClass]="{ expanded: isMenuExpanded }">
  @if (menuSettings$ | async; as menuSettings) {
    <div class="floating-container" [ngClass]="{ expanded: isMenuExpanded }">
      <!-- menu header component -->
      <div class="menu-header" [ngClass]="{ scrolled: isScrolled, expanded: isMenuExpanded }">
        <div class="menu-header-row">
          @if ((parentRoute$ | async) == null) {
            <!-- mobile or collapsed -->
            @if (isMobile || !isMenuExpanded) {
              <div class="menu-header-logo-mobile">
                <a [routerLink]="rootRoute" class="mobile-logo">
                  <img src="assets/svg/pointee_mobile.svg" alt="Pointee mobile logo" />
                </a>
              </div>
            }
            <!-- expanded -->
            @if (isMenuExpanded) {
              <!-- desktop logo -->
              <div class="menu-header-logo">
                <a [routerLink]="rootRoute" class="brand-logo">
                  <img src="assets/svg/pointee_primary.svg" alt="Pointee brand logo" />
                </a>
              </div>
              <!-- action button -->
              @if (allowOrganizationSelect) {
                <button
                  class="icon-btn organization-switcher-button"
                  [attr.data-testid]="'main-menu-organizations-button'"
                  [preventFocus]="'OnceAfterClick'"
                  [tooltip]="'Switch to another organization'"
                  (click)="displayOrganizationSelect()"
                >
                  <svg-icon key="organization-24" size="md" />
                </button>
              }
            }
          }
          @if (parentRoute$ | async; as parentRoute) {
            <div class="menu-subheader-container">
              <a [routerLink]="parentRoute" class="icon-btn link" (click)="reloadPage(parentRoute)">
                @if (isMenuExpanded) {
                  <svg-icon key="exit" fontSize="16px" /><span>&nbsp;Exit&nbsp;{{ firstLabel$ | async }}</span>
                } @else {
                  <svg-icon key="exit" fontSize="24px" />
                }
              </a>
            </div>
          }
        </div>

        @if (!isMenuExpanded && allowOrganizationSelect) {
          <div class="menu-header-row">
            <button class="icon-btn" [attr.data-testid]="'main-menu-organizations-button'" [tooltip]="'Switch to another organization'" (click)="displayOrganizationSelect()">
              <svg-icon key="organization-solid" size="lg" />
            </button>
          </div>
        }
        <!-- tenant selector -->
        @if ((showTenants$ | async) && (tenants?.length > 1 || showAllWorkflows)) {
          @if (isMenuExpanded) {
            <mat-select name="selectedTenantId" class="organization-switcher" [attr.data-testid]="'main-menu-tenants-select'" [value]="selectedTenant" (valueChange)="onTenantChanged($event)">
              <mat-select-trigger>
                <svg-icon key="tenant" style="color: var(--font-light)" />
                <span class="menu-select-overflow-ellipsis">{{ selectedTenant?.label }}</span>
              </mat-select-trigger>
              @if (showAllWorkflows) {
                <mat-option [value]="allWorkflows">{{ allWorkflows.label }}</mat-option>
              }
              @if (tenants?.length > 1) {
                <mat-option [value]="allTenants">{{ allTenants.label }}</mat-option>
              }
              @for (tenant of tenants; track tenant) {
                <mat-option [value]="tenant">{{ tenant.label }}</mat-option>
              }
            </mat-select>
          } @else {
            <button class="icon-btn" [attr.data-testid]="'main-menu-tenants-button'" (click)="openTenantSelect()" [tooltip]="'Switch to another tenant'">
              <svg-icon key="tenant-24" size="lg" />
            </button>
          }
        }
      </div>
      <!-- menu body content -->
      <div #menuBodyContainer id="menu-body" [ngClass]="{ expanded: isMenuExpanded }" (scroll)="onBodyContainerScroll($event)">
        <div class="menu-body-container">
          <!-- menu sections -->
          @for (section of menuSettings.sections; track $index; let isFirst = $first) {
            <div [ngClass]="{ 'action-wrapper': section.type === 'actions' }">
              <div class="menu-section">
                @if (section.type === 'items') {
                  <span class="menu-section-title">{{ section.label }}</span>
                }
                @if ((!isFirst || menuSettings.sections.length === 1) && (!isMenuExpanded || section.type === 'actions')) {
                  <span class="separator"></span>
                }
                @for (menuItem of section.items; track $index) {
                  <a
                    class="menu-item"
                    [attr.data-testid]="'main-menu-' + (menuItem.pageType?.toLowerCase() ?? menuItem.dataTestId)"
                    [routerLink]="menuItem.route || undefined"
                    [routerLinkActive]="['active']"
                    [routerLinkActiveOptions]="{ exact: false }"
                    [ngClass]="{ 'active-manual': menuItem.active || isMenuItemActive(menuItem), disabled: menuItem.disabled }"
                    [style.pointer-events]="menuItem.disabled ? 'none' : 'auto'"
                    [style.display]="menuItem.isHidden === true ? 'none' : 'flex'"
                    [tooltip]="menuItem.label"
                    [display]="!isMenuExpanded"
                    (click)="menuItem.onClick && menuItem.onClick($event)"
                  >
                    <span class="menu-item-link">
                      <svg-icon key="{{ isMenuExpanded ? menuItem.icon + '-solid' : menuItem.icon }}" [ngClass]="{ large: !isMenuExpanded }" />
                      @if (isMenuExpanded) {
                        <span class="item-label menu-item-label prevent-select">{{ menuItem.label }}</span>
                      }
                      @if (menuItem.badge$ | async; as badge) {
                        @if (isBadgeVisible(menuItem, badge)) {
                          @if (menuItem.badgeType === 'warning') {
                            <div class="badge warning" [class.large]="isMenuExpanded">{{ badge }}</div>
                          }
                          @if (menuItem.badgeType === 'count' && isMenuExpanded) {
                            <span class="menu-item-count">{{ badge }}</span>
                          }
                        }
                      }
                    </span>
                  </a>
                }
              </div>
            </div>
          }
        </div>
      </div>
      <!-- end of menu body content -->
      <!-- menu footer -->
      <div id="menu-footer" [ngClass]="{ scrolled: isNextScrolled, expanded: isMenuExpanded }">
        <!-- user menu -->
        @if (isMenuExpanded) {
          <div class="user-menu-button" [attr.data-testid]="'main-menu-profile-button'" [matMenuTriggerFor]="profileMenu">
            <svg-icon key="account-24" size="lg" />
            <span class="account-name">{{ user?.userName }}</span>
            <svg-icon key="chevron-down" />
          </div>
        } @else {
          <div class="footer-action-buttons">
            <button class="icon-btn" [attr.data-testid]="'main-menu-profile-button'" [matMenuTriggerFor]="profileMenu" [tooltip]="'Account'">
              <svg-icon key="account-24" size="lg" />
            </button>
          </div>
        }
        <!-- action button -->
        @if (allowExpandMenu) {
          <div class="footer-collapse-button-container">
            <button class="icon-btn" [attr.data-testid]="'main-menu-expand-button'" (click)="toggleMenuExpanded()" [tooltip]="!isMenuExpanded ? 'Expand menu' : 'Collapse menu'" tooltipHideOnClick>
              @if (isMenuExpanded) {
                <svg-icon key="expand-24-solid" />
              } @else {
                <svg-icon key="expand-24" size="lg" />
              }
            </button>
          </div>
        }
      </div>
    </div>
  }
</aside>

<mat-menu #profileMenu="matMenu" class="pointee-mat-menu">
  <ng-template matMenuContent>
    <div (click)="$event.stopPropagation(); $event.preventDefault()" class="flex-col p-l-16 p-r-16">
      <h4>{{ user.userName }}</h4>
      <p>{{ user.email }}</p>
    </div>
    <hr class="no-margin-padding" />
    @if (isSystemAdmin) {
      <button mat-menu-item (click)="gotoAdministrationPage()"><svg-icon key="admin" />Pointee administration</button>
      <button mat-menu-item (click)="gotoDesignSystemOverview()"><svg-icon key="design-system" />Design system</button>
      <hr class="no-margin-padding" />
    }
    <!-- <button mat-menu-item *ngIf="areNotificationSettingsEnabled" (click)="openNotificationSettings()"><svg-icon key="alarm-bell" />Notifications settings</button> -->
    @if (isUserPasswordLoginEnabled) {
      <button mat-menu-item (click)="changePassword()"><svg-icon key="password" />Change password</button>
    }
    <button mat-menu-item (click)="signOut()"><svg-icon key="logout-16" />Sign out</button>
  </ng-template>
</mat-menu>
